import { useEffect, useState } from "react";

import { fetchSeatmap } from "../api";
import { clockOf } from "../format";
import { fromSeatmap } from "../plan";
import type { Seatmap, SearchResponse } from "../types";
import { Dialog } from "./Dialog";
import { PlanLegend, SeatPlan } from "./SeatPlan";

type Props = {
  response: SearchResponse;
  movieTitle: string;
  onAdjust: () => void;
  onClose: () => void;
};

export function ResultsDialog({ response, movieTitle, onAdjust, onClose }: Props) {
  const [selected, setSelected] = useState(0);
  const [seatmap, setSeatmap] = useState<Seatmap | null>(null);
  const [error, setError] = useState<string | null>(null);

  const best = response.results[selected];

  useEffect(() => {
    if (!best?.showtime.cinemaId || !best.showtime.showingId) return;

    let cancelled = false;
    setSeatmap(null);
    setError(null);

    fetchSeatmap(best.showtime.cinemaId, best.showtime.showingId)
      .then((plan) => !cancelled && setSeatmap(plan))
      .catch((cause: Error) => !cancelled && setError(cause.message));

    return () => {
      cancelled = true;
    };
  }, [best]);

  if (response.results.length === 0) {
    return (
      <Dialog
        title="Aucun résultat"
        subtitle={`${response.examined} séance${response.examined > 1 ? "s" : ""} examinée${response.examined > 1 ? "s" : ""}`}
        width="narrow"
        onClose={onClose}
        footer={
          <>
            <span className="grow" />
            <button type="button" className="btn btn-primary" onClick={onAdjust}>
              Ajuster les critères
            </button>
          </>
        }
      >
        <div className="empty">
          <p className="empty-title">Aucune séance ne peut accueillir ce groupe</p>
          <p className="empty-text">
            {response.skipped > 0
              ? `${response.skipped} séance${response.skipped > 1 ? "s ont été écartées" : " a été écartée"} par le filtre de taille de salle — essayez « Peu importe ».`
              : "Essayez d'élargir la zone ou la disposition du groupe."}
          </p>
        </div>
      </Dialog>
    );
  }

  const seatList = best.seats.map((seat) => `${seat.row}${seat.name}`);

  return (
    <Dialog
      title={`${response.results.length} séance${response.results.length > 1 ? "s classées" : " trouvée"}`}
      subtitle={`${movieTitle} · ${response.examined} séance${response.examined > 1 ? "s" : ""} examinée${response.examined > 1 ? "s" : ""}${response.partial ? " (lecture écourtée)" : ""}`}
      width="wide"
      onClose={onClose}
      bodyless
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onAdjust}>
            Ajuster
          </button>
          <span className="grow" />
          <a
            className="btn btn-primary"
            href={best.showtime.bookingUrl}
            target="_blank"
            rel="noreferrer"
          >
            Réserver sur pathe.fr
          </a>
        </>
      }
    >
      <div className="results">
        <ol className="result-list as-tabs">
          {response.results.map((result, index) => (
            <li key={result.showtime.bookingUrl}>
              <button
                type="button"
                className={index === selected ? "result-item is-on" : "result-item"}
                onClick={() => setSelected(index)}
              >
                <span className="result-rank">#{index + 1}</span>
                <span className="result-time">{clockOf(result.showtime.time)}</span>
                <span className="result-meta">
                  {result.showtime.version} · Salle {result.showtime.auditorium} ·{" "}
                  {Math.round(result.occupancy * 100)} %
                </span>
                <span className="result-meta" style={{ color: "var(--accent)" }}>
                  {result.seats.map((seat) => `${seat.row}${seat.name}`).join(", ")}
                </span>
              </button>
            </li>
          ))}
        </ol>

        <div className="result-detail">
          {!best.zoneRespected && (
            <span className="badge-warn">HORS ZONE DEMANDÉE — meilleur compromis</span>
          )}

          <div className="pick-card">
            <div className="pick-head">
              <span className="pick-time">{clockOf(best.showtime.time)}</span>
              <span className="pick-where">
                {best.showtime.version} · {best.room}
              </span>
              <span className="pick-fill">{Math.round(best.occupancy * 100)} % rempli</span>
            </div>

            <div className="seat-chips">
              {seatList.map((seat) => (
                <span className="seat-chip" key={seat}>
                  {seat}
                </span>
              ))}
            </div>

            <p className="pick-phrase">
              {best.seats.length} place{best.seats.length > 1 ? "s" : ""} en {seatList.join(", ")} ·{" "}
              {Math.round(best.comfort * 100)} % de sièges vides autour · {best.free} libres dans la
              salle.
            </p>
          </div>

          {!seatmap && !error && (
            <div className="loading">
              <div className="spinner" />
              <span className="state">lecture du plan…</span>
            </div>
          )}
          {error && <p className="state is-error">{error}</p>}
          {seatmap && (
            <div style={{ marginTop: 18 }}>
              <SeatPlan plan={fromSeatmap(seatmap, best.seats)} />
              <PlanLegend withPicked />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
